import Card from '@carvana/ds-card';
import LinkButton from '@carvana/ds-link-button';
import { shadow } from '@carvana/ds-styles';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';
import type { Summary } from '|/app/services';
import CardBrandIcon, { isKnownCardBrand } from '|/components/carvana-ds/card-brand-icon';
import { SevenDayBanner } from '|/components/seven-day-banner';
import ApplePayMark from '|/icons/apple-pay-mark.svg';
import GooglePayMark from '|/icons/google-pay-mark.svg';
import MicrOnUs from '|/icons/micr-on-us.svg';
import MicrTransit from '|/icons/micr-transit.svg';
import {
  derivePriceIsRightView,
  type PriceIsRightPaymentMethodView
} from './derive-price-is-right-view';
import styles from './price-is-right-content.module.css';
import type { GetPaymentSummaryResponse } from '../services/purchase-payments-api.types';

const shippingDetails = 'Transporting the vehicle to a Carvana location near you.';
const cardShadow = shadow.lg
  .map(({ x, y, blur, spread, color }) => `${x}px ${y}px ${blur}px ${spread}px ${color}`)
  .join(', ');

export interface PriceIsRightContentProps {
  paymentSummary: GetPaymentSummaryResponse;
  reviewSummary: Summary | null;
  onViewPriceDetails: () => void;
}

const PaymentMethod = ({ method }: { method: PriceIsRightPaymentMethodView }) => {
  switch (method.type) {
    case 'bank-account':
      return (
        <span
          className={styles.paymentMethodValue}
          aria-label={method.mask ? `Account ending in ${method.mask}` : 'Bank account'}
        >
          Account
          <MicrTransit width={9} height={12} aria-hidden="true" />
          {method.mask ? ` ····${method.mask}` : null}
          <MicrOnUs width={9} height={9} aria-hidden="true" />
        </span>
      );

    case 'card':
      return (
        <span
          className={styles.paymentMethodValue}
          aria-label={`${method.cardBrand ?? 'Card'}${
            method.mask ? ` ending in ${method.mask}` : ''
          }`}
        >
          <CardBrandIcon brand={method.cardBrand ?? undefined} />
          {!isKnownCardBrand(method.cardBrand ?? undefined) && 'Card'}
          {method.mask ? ` ····${method.mask}` : null}
        </span>
      );

    case 'apple-pay':
      return (
        <span className={styles.paymentMethodValue}>
          <ApplePayMark className={styles.walletMark} role="img" aria-label={method.label} />
        </span>
      );

    case 'google-pay':
      return (
        <span className={styles.paymentMethodValue}>
          <GooglePayMark className={styles.walletMark} role="img" aria-label={method.label} />
        </span>
      );

    default:
      return <span className={styles.paymentMethodValue}>{method.label}</span>;
  }
};

export const PriceIsRightContent = ({
  paymentSummary,
  reviewSummary,
  onViewPriceDetails
}: PriceIsRightContentProps) => {
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  const view = derivePriceIsRightView(paymentSummary, reviewSummary);

  return (
    <Card
      showBorder={false}
      margin="sm"
      className={styles.card}
      classes={{ content: styles.cardInner }}
      style={{ boxShadow: cardShadow }}
    >
      <div className={styles.cardContent}>
        <div className={styles.vehicle}>
          {view.vehicle.imageUrl && (
            <img
              className={styles.vehicleImage}
              src={view.vehicle.imageUrl}
              alt={view.vehicle.title}
            />
          )}

          <div className={styles.vehicleCopy}>
            <h3 className={styles.vehicleTitle}>{view.vehicle.title}</h3>
            {view.vehicle.trim && <p className={styles.vehicleTrim}>{view.vehicle.trim}</p>}
          </div>
        </div>

        <section className={styles.priceSection} aria-label="Price summary">
          <div className={styles.headline}>
            <h4 className={styles.headlineLabel}>{view.headline.label}</h4>
            <p className={styles.headlineAmount}>{view.headline.formattedAmount}</p>
          </div>

          {view.breakdownItems.length > 0 && (
            <div className={styles.breakdown}>
              <p className={styles.includesLabel}>Includes:</p>

              <div className={styles.breakdownItems}>
                {view.breakdownItems.map(item =>
                  item.expandable ? (
                    <div className={styles.shippingItem} key={item.id}>
                      <button
                        type="button"
                        className={styles.shippingToggle}
                        aria-expanded={isShippingExpanded}
                        onClick={() => {
                          setIsShippingExpanded(expanded => !expanded);
                        }}
                        data-analytics-id="pp_complete_purchase_price_is_right_toggle_shipping_details"
                      >
                        {item.formattedAmount} {item.label}
                        <ChevronDown className={styles.chevron} size={16} aria-hidden="true" />
                      </button>

                      {isShippingExpanded && (
                        <p className={styles.shippingDetails}>{shippingDetails}</p>
                      )}
                    </div>
                  ) : (
                    <p
                      className={
                        item.tone === 'success' ? styles.breakdownItemSuccess : styles.breakdownItem
                      }
                      key={item.id}
                    >
                      {item.formattedAmount} {item.label}
                    </p>
                  )
                )}
              </div>
            </div>
          )}

          <LinkButton
            type="button"
            size="xs"
            underline={false}
            onClick={onViewPriceDetails}
            data-analytics-id="pp_complete_purchase_price_is_right_view_price_details"
          >
            See Full Price Breakdown
          </LinkButton>
        </section>

        {view.paymentMethod && (
          <section
            className={styles.paymentMethod}
            aria-label={
              view.paymentMethod.purpose === 'refund' ? 'Refund destination' : 'Payment method'
            }
          >
            <h4 className={styles.paymentMethodLabel}>Payment method</h4>
            <PaymentMethod method={view.paymentMethod} />
          </section>
        )}
      </div>

      <SevenDayBanner compact />
    </Card>
  );
};

export default PriceIsRightContent;
